import { __ } from "@wordpress/i18n";
import { useSettings } from "../../context/SettingsContext";
import {
    Toggle,
    SettingRow,
    SectionCard,
    NumberInput,
    ButtonGroup,
    ColorInput,
    ProBadge,
} from "../ui/index";
import { isPremium } from "../../utils/isPremium";

const PAGINATION_TYPES = [
    { value: "thumbs", label: __("Thumbnails", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "dots",   label: __("Dots", "wpbean-product-gallery-slider-for-woocommerce") },
];

const BUTTON_POSITIONS = [
    { value: "bottom-right", label: __("Bottom Right", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "bottom-left",  label: __("Bottom Left", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "top-right",    label: __("Top Right", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "top-left",     label: __("Top Left", "wpbean-product-gallery-slider-for-woocommerce") },
];

const LIGHTBOX_TRANSITIONS = [
    { value: "slide", label: __("Slide", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "fade",  label: __("Fade", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "zoom",  label: __("Zoom", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "none",  label: __("None", "wpbean-product-gallery-slider-for-woocommerce") },
];

export default function LightboxPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);
    const enabled = settings.lightbox;

    return (
        <div className="space-y-5">
            <SectionCard
                title={__("Lightbox", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Full-screen image viewer overlay.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Enable Lightbox", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Allow product images to be opened in a full-screen lightbox.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.lightbox}
                        onChange={(v) => s("lightbox", v)}
                    />
                </SettingRow>
            </SectionCard>

            {enabled && (
                <>
                    <SectionCard
                        title={__("Opening Behaviour", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("How and when the lightbox is triggered.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <SettingRow
                            label={__("Show Expand Button", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Display a fullscreen icon button on the main image.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxOnClick}
                                onChange={(v) => s("lightboxOnClick", v)}
                            />
                        </SettingRow>

                        {settings.lightboxOnClick && (
                            <SettingRow
                                label={__("Expand Button Position", "wpbean-product-gallery-slider-for-woocommerce")}
                                description={__("Corner of the main image where the expand button is placed.", "wpbean-product-gallery-slider-for-woocommerce")}
                            >
                                <ButtonGroup
                                    options={BUTTON_POSITIONS}
                                    value={settings.lightboxButtonPosition}
                                    onChange={(v) => s("lightboxButtonPosition", v)}
                                />
                            </SettingRow>
                        )}

                        <SettingRow
                            label={__("Open on Image Click", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Also open the lightbox when clicking the main product image directly.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxOpenOnImageClick}
                                onChange={(v) =>
                                    s("lightboxOpenOnImageClick", v)
                                }
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Close on Overlay Click", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Close the lightbox when clicking the dark background overlay.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxCloseOnOverlay}
                                onChange={(v) => s("lightboxCloseOnOverlay", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Keyboard Navigation", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Use arrow keys to navigate and Escape to close the lightbox.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxKeyboard}
                                onChange={(v) => s("lightboxKeyboard", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={
                                <span className="inline-flex items-center gap-1.5">
                                    {__("Swipe / Drag Navigation", "wpbean-product-gallery-slider-for-woocommerce")}
                                    {!isPremium() && <ProBadge />}
                                </span>
                            }
                            description={__("Drag or swipe the lightbox image to navigate between images. Works on desktop (mouse drag) and mobile (touch swipe).", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxSwipe}
                                onChange={(v) => s("lightboxSwipe", v)}
                                disabled={!isPremium()}
                            />
                        </SettingRow>
                    </SectionCard>

                    <SectionCard
                        title={__("Display", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("What elements are shown inside the lightbox.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <SettingRow
                            label={
                                <span className="inline-flex items-center gap-1.5">
                                    {__("Single Image Mode", "wpbean-product-gallery-slider-for-woocommerce")}
                                    {!isPremium() && <ProBadge />}
                                </span>
                            }
                            description={__("Show only the clicked image in the lightbox instead of the full gallery.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxSingleImage}
                                onChange={(v) => s("lightboxSingleImage", v)}
                                disabled={!isPremium()}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Overlay Background", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Background color and opacity of the dark backdrop behind the lightbox image.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <ColorInput
                                value={settings.lightboxOverlayColor}
                                onChange={(v) => s("lightboxOverlayColor", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Navigation Arrows", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show previous and next arrow buttons for navigating between images.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxShowArrows}
                                onChange={(v) => s("lightboxShowArrows", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={
                                <span className="inline-flex items-center gap-1.5">
                                    {__("Loop Images", "wpbean-product-gallery-slider-for-woocommerce")}
                                    {!isPremium() && <ProBadge />}
                                </span>
                            }
                            description={__("Loop back to the first image after reaching the last.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxLoop}
                                onChange={(v) => s("lightboxLoop", v)}
                                disabled={!isPremium()}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Image Counter", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__('Show current position indicator (e.g. "2 / 5").', "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxShowCounter}
                                onChange={(v) => s("lightboxShowCounter", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Image Caption", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show the image alt text as a caption below the image.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxShowCaption}
                                onChange={(v) => s("lightboxShowCaption", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Pagination", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show navigation indicators below the lightbox image.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxShowThumbnails}
                                onChange={(v) => s("lightboxShowThumbnails", v)}
                            />
                        </SettingRow>

                        {settings.lightboxShowThumbnails && (
                            <SettingRow
                                label={__("Pagination Style", "wpbean-product-gallery-slider-for-woocommerce")}
                                description={__("Display as thumbnail images or simple dot indicators.", "wpbean-product-gallery-slider-for-woocommerce")}
                            >
                                <ButtonGroup
                                    options={PAGINATION_TYPES}
                                    value={settings.lightboxPaginationType}
                                    onChange={(v) =>
                                        s("lightboxPaginationType", v)
                                    }
                                />
                            </SettingRow>
                        )}

                        {settings.lightboxShowThumbnails &&
                            settings.lightboxPaginationType === "thumbs" && (
                                <SettingRow
                                    label={__("Thumbnail Radius", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Rounded corner radius of thumbnail strip items.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <NumberInput
                                        value={
                                            settings.lightboxThumbBorderRadius
                                        }
                                        onChange={(v) =>
                                            s("lightboxThumbBorderRadius", v)
                                        }
                                        min={0}
                                        max={50}
                                        unit="px"
                                    />
                                </SettingRow>
                            )}

                        <SettingRow
                            label={__("Image Radius", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Rounded corner radius of the main lightbox image.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <NumberInput
                                value={settings.lightboxImageBorderRadius}
                                onChange={(v) =>
                                    s("lightboxImageBorderRadius", v)
                                }
                                min={0}
                                max={80}
                                unit="px"
                            />
                        </SettingRow>
                    </SectionCard>

                    <SectionCard
                        title={__("Toolbar Buttons", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Buttons shown in the lightbox toolbar.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <SettingRow
                            label={__("Zoom In / Out", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show zoom in and zoom out buttons in the lightbox toolbar.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxZoomIn}
                                onChange={(v) => s("lightboxZoomIn", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={
                                <span className="inline-flex items-center gap-1.5">
                                    {__("Mouse Scroll Zoom", "wpbean-product-gallery-slider-for-woocommerce")}
                                    {!isPremium() && <ProBadge />}
                                </span>
                            }
                            description={__("Allow scrolling the mouse wheel to zoom in and out on the lightbox image.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxScrollZoom}
                                onChange={(v) => s("lightboxScrollZoom", v)}
                                disabled={!isPremium()}
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Autoplay Button", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show the slideshow play/pause toggle button in the toolbar.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxShowAutoplayButton}
                                onChange={(v) =>
                                    s("lightboxShowAutoplayButton", v)
                                }
                            />
                        </SettingRow>

                        <SettingRow
                            label={__("Fullscreen", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Show the fullscreen toggle button.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxFullscreen}
                                onChange={(v) => s("lightboxFullscreen", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label={
                                <span className="inline-flex items-center gap-1.5">
                                    {__("Image Tools", "wpbean-product-gallery-slider-for-woocommerce")}
                                    {!isPremium() && <ProBadge />}
                                </span>
                            }
                            description={__("Show the center toolbar with rotate, flip, and 1:1 zoom buttons.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxImageTools}
                                onChange={(v) => s("lightboxImageTools", v)}
                                disabled={!isPremium()}
                            />
                        </SettingRow>

                        <SettingRow
                            label={
                                <span className="inline-flex items-center gap-1.5">
                                    {__("Share Button", "wpbean-product-gallery-slider-for-woocommerce")}
                                    {!isPremium() && <ProBadge />}
                                </span>
                            }
                            description={__("Show a share button in the toolbar that opens a menu with sharing and download options.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxShare}
                                onChange={(v) => s("lightboxShare", v)}
                                disabled={!isPremium()}
                            />
                        </SettingRow>

                        {settings.lightboxShare && (
                            <>
                                <SettingRow
                                    label={__("Share on Facebook", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Include 'Share on Facebook' in the share menu.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <Toggle
                                        value={settings.lightboxShareFacebook}
                                        onChange={(v) =>
                                            s("lightboxShareFacebook", v)
                                        }
                                    />
                                </SettingRow>

                                <SettingRow
                                    label={__("Tweet", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Include 'Tweet' (Twitter/X) in the share menu.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <Toggle
                                        value={settings.lightboxShareTwitter}
                                        onChange={(v) =>
                                            s("lightboxShareTwitter", v)
                                        }
                                    />
                                </SettingRow>

                                <SettingRow
                                    label={__("Pin it", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Include 'Pin it' (Pinterest) in the share menu.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <Toggle
                                        value={settings.lightboxSharePinterest}
                                        onChange={(v) =>
                                            s("lightboxSharePinterest", v)
                                        }
                                    />
                                </SettingRow>

                                <SettingRow
                                    label={__("Download Image", "wpbean-product-gallery-slider-for-woocommerce")}
                                    description={__("Include 'Download image' in the share menu.", "wpbean-product-gallery-slider-for-woocommerce")}
                                >
                                    <Toggle
                                        value={settings.lightboxShareDownload}
                                        onChange={(v) =>
                                            s("lightboxShareDownload", v)
                                        }
                                    />
                                </SettingRow>
                            </>
                        )}
                    </SectionCard>

                    <SectionCard
                        title={__("Animation", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Transition effect and speed for lightbox.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <SettingRow
                            label={__("Effect", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Animation style when navigating between images.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <ButtonGroup
                                options={LIGHTBOX_TRANSITIONS}
                                value={settings.lightboxTransition}
                                onChange={(v) => s("lightboxTransition", v)}
                            />
                        </SettingRow>

                        {settings.lightboxTransition !== "none" && (
                            <SettingRow
                                label={__("Duration", "wpbean-product-gallery-slider-for-woocommerce")}
                                description={__("Length of the open/close and navigation animations.", "wpbean-product-gallery-slider-for-woocommerce")}
                            >
                                <NumberInput
                                    value={settings.lightboxAnimationDuration}
                                    onChange={(v) =>
                                        s("lightboxAnimationDuration", v)
                                    }
                                    min={0}
                                    max={2000}
                                    unit="ms"
                                    width="w-24"
                                />
                            </SettingRow>
                        )}

                        <SettingRow
                            label={__("Zoom From Image", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Animate the lightbox opening and closing by zooming from/to the source image position.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxZoomOrigin}
                                onChange={(v) => s("lightboxZoomOrigin", v)}
                            />
                        </SettingRow>
                    </SectionCard>

                    <SectionCard
                        title={__("Slideshow", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Auto-advance through images automatically.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <SettingRow
                            label={__("Enable Autoplay", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Automatically advance to the next image at a set interval.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <Toggle
                                value={settings.lightboxAutoplay}
                                onChange={(v) => s("lightboxAutoplay", v)}
                            />
                        </SettingRow>

                        {settings.lightboxAutoplay && (
                            <SettingRow
                                label={__("Slide Interval", "wpbean-product-gallery-slider-for-woocommerce")}
                                description={__("Time between slides during autoplay.", "wpbean-product-gallery-slider-for-woocommerce")}
                            >
                                <NumberInput
                                    value={settings.lightboxAutoplayDelay}
                                    onChange={(v) =>
                                        s("lightboxAutoplayDelay", v)
                                    }
                                    min={500}
                                    max={30000}
                                    unit="ms"
                                    width="w-24"
                                />
                            </SettingRow>
                        )}
                    </SectionCard>
                </>
            )}
        </div>
    );
}
